import { ArrowDown, ArrowRight, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { mediaUrl } from "lib/media";

const gregPhoto = mediaUrl("website_files/GregsFACE.png");
const resumePdf = mediaUrl("website_files/Resume2026.pdf");

const software = [
  {
    name: "Houdini",
    image: mediaUrl("website_files/logos/houdini.png"),
  },
  {
    name: "Nuke",
    image: mediaUrl("website_files/logos/nuke.png"),
  },
  {
    name: "Maya",
    image: mediaUrl("website_files/logos/maya.png"),
  },
  {
    name: "After Effects",
    image: mediaUrl("website_files/logos/aftereffect.png"),
  },
  {
    name: "Mocha",
    image: mediaUrl("website_files/logos/mocha.png"),
  },
  {
    name: "Photoshop",
    image: mediaUrl("website_files/logos/photoshop.png"),
  },
  {
    name: "RealFlow",
    image: mediaUrl("website_files/logos/realflow.png"),
  },
  {
    name: "ZBrush",
    image: mediaUrl("website_files/logos/zbrush.png"),
  },
];

export const About = () => {
  return (
    <div className="bg-background text-foreground">
      <section className="border-b border-white/10">
        <div className="page-shell grid min-h-[calc(100svh-82px)] items-center gap-14 py-16 lg:grid-cols-12 lg:gap-12 lg:py-24">
          <div className="lg:col-span-7">
            <p className="eyebrow reveal-up">About Gregory</p>
            <h1 className="page-title reveal-up reveal-delay-1 mt-7 max-w-5xl">
              Crafting effects with
              <span className="block text-white/30">
                purpose and precision.
              </span>
            </h1>
            <div className="reveal-up reveal-delay-2 mt-10 grid gap-8 sm:grid-cols-[1fr_auto] sm:items-end">
              <p className="max-w-2xl text-base leading-8 text-white/60 sm:text-lg">
                I’m a Los Angeles–based VFX artist with more than seven years of
                experience building procedural effects, 3D environments, and
                composites. My work is grounded in Houdini and Nuke, with an
                emphasis on creating believable visuals through thoughtful,
                efficient systems.
              </p>
              <ArrowDown className="hidden h-6 w-6 text-ember sm:block" />
            </div>
            <div className="reveal-up reveal-delay-3 mt-10 flex flex-wrap gap-3">
              <a
                className="button-primary"
                href={resumePdf}
                target="_blank"
                rel="noreferrer"
              >
                Open résumé
                <Download className="h-4 w-4" />
              </a>
              <Link className="button-secondary group" to="/demo-reel">
                View my work
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          <div className="relative lg:col-span-5 lg:pl-8">
            <div className="absolute -bottom-7 -left-7 h-36 w-36 border-b border-l border-ember/60" />
            <div className="relative aspect-[4/5] overflow-hidden bg-white/5">
              <img
                alt="Gregory Kalamdaryan"
                src={gregPhoto}
                className="h-full w-full object-cover object-center grayscale-[18%]"
                loading="eager"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50">
                    Based in
                  </p>
                  <p className="mt-1 text-lg font-semibold">Los Angeles, CA</p>
                </div>
                <span className="h-2 w-2 rounded-full bg-ember shadow-[0_0_16px_rgba(255,92,54,0.9)]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="page-shell py-24 sm:py-32">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-4">
            <p className="eyebrow">Core toolkit</p>
            <h2 className="section-title mt-5">Tools that serve the image.</h2>
            <p className="mt-6 max-w-md text-sm leading-7 text-white/50">
              Software is only part of the process. The goal is always a clear
              visual idea, executed with the right mix of artistry and technical
              control.
            </p>
          </div>
          <div className="grid grid-cols-2 border-l border-t border-white/10 sm:grid-cols-4 lg:col-span-8">
            {software.map((tool) => (
              <div
                key={tool.name}
                className="group flex min-h-40 flex-col items-center justify-center border-b border-r border-white/10 p-6 transition hover:bg-white/[0.035]"
              >
                <img
                  src={tool.image}
                  alt=""
                  aria-hidden="true"
                  className="h-12 w-12 object-contain grayscale transition duration-300 group-hover:grayscale-0"
                  loading="lazy"
                />
                <p className="mt-5 text-xs font-semibold uppercase tracking-[0.16em] text-white/50 transition group-hover:text-white">
                  {tool.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.025]">
        <div className="page-shell py-24 sm:py-32">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <p className="eyebrow">Experience</p>
              <h2 className="section-title mt-5">Résumé</h2>
            </div>
            <a
              className="text-link"
              href={resumePdf}
              target="_blank"
              rel="noreferrer"
            >
              Open PDF
              <Download className="h-4 w-4" />
            </a>
          </div>

          <div className="mx-auto mt-12 max-w-4xl overflow-hidden border border-white/10 bg-white p-2 shadow-2xl shadow-black/40 sm:p-4">
            <object
              data={`${resumePdf}#view=FitH`}
              type="application/pdf"
              width="100%"
              className="block h-[760px] bg-white md:h-[1080px]"
              aria-label="Gregory Kalamdaryan résumé"
            >
              <div className="flex min-h-80 flex-col items-center justify-center gap-5 bg-[#f4f4f2] px-6 text-center text-black">
                <p>Your browser could not display the résumé preview.</p>
                <a
                  className="inline-flex h-11 items-center bg-black px-5 text-xs font-bold uppercase tracking-[0.12em] text-white"
                  href={resumePdf}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open résumé PDF
                </a>
              </div>
            </object>
          </div>
        </div>
      </section>
    </div>
  );
};
